// apiClient.js
// Single, central place for all authenticated API calls.
// Everything (Forms today, MIR/QC/NCR later if they go live) should import
// `apiFetch` from here instead of calling fetch()/axios directly.

const BASE_URL = "https://konstruct.world";
const REFRESH_URL = `${BASE_URL}/users/token/refresh/`; // adjust if this 404s — see console warning

// Common key-name patterns apps use after login. Add yours here if it's
// not already covered — check DevTools → Application → Local Storage.
const ACCESS_KEY_CANDIDATES = ["access", "accessToken", "access_token", "token"];
const REFRESH_KEY_CANDIDATES = ["refresh", "refreshToken", "refresh_token"];

function findFirstStoredValue(candidates) {
  for (const key of candidates) {
    const v = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (v) return { key, value: v };
  }
  return null;
}

function getAccessToken() {
  const found = findFirstStoredValue(ACCESS_KEY_CANDIDATES);
  if (!found) {
    console.warn(
      "[apiClient] No access token found under any of:",
      ACCESS_KEY_CANDIDATES,
      "— check what key your login code actually uses and add it to ACCESS_KEY_CANDIDATES."
    );
    return null;
  }
  return found.value;
}

function getRefreshToken() {
  const found = findFirstStoredValue(REFRESH_KEY_CANDIDATES);
  return found ? found.value : null;
}

function storeNewAccessToken(newAccess) {
  // Write back under whichever key the access token was originally found under,
  // so the rest of the app (if it reads the token elsewhere) still finds it.
  const found = findFirstStoredValue(ACCESS_KEY_CANDIDATES);
  const key = found ? found.key : "access";
  const storage = localStorage.getItem(key) !== null ? localStorage : sessionStorage;
  storage.setItem(key, newAccess);
}

let refreshInFlight = null; // dedupes concurrent 401s into a single refresh call

async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) {
    console.error("[apiClient] No refresh token available — user needs to log in again.");
    return null;
  }

  if (!refreshInFlight) {
    refreshInFlight = fetch(REFRESH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    })
      .then(async (res) => {
        if (!res.ok) {
          console.error(
            `[apiClient] Refresh failed (${res.status}) at ${REFRESH_URL}. ` +
            `If this is a 404, the refresh path is probably different — check your Network tab.`
          );
          return null;
        }
        const data = await res.json();
        // SimpleJWT refresh response is typically { access: "..." }
        const newAccess = data.access || data.access_token;
        if (newAccess) storeNewAccessToken(newAccess);
        return newAccess || null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/**
 * apiFetch — use this instead of fetch()/axios everywhere.
 * Attaches Authorization: Bearer <token>, and on a 401 automatically
 * refreshes the access token once and retries the original request.
 */
export async function apiFetch(path, options = {}) {
  const url = path.startsWith("http") ? path : `${BASE_URL}${path}`;

  const doRequest = (token) =>
    fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

  let token = getAccessToken();
  let res = await doRequest(token);

  if (res.status === 401) {
    console.warn("[apiClient] Got 401, attempting token refresh…");
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await doRequest(newToken);
    } else {
      console.error("[apiClient] Refresh failed or unavailable — request stays 401.");
    }
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API request failed (${res.status}) for ${url}: ${text}`);
  }

  return res.json();
}